import React, { Component } from 'react';
import {Link} from 'react-router-dom';
import HttpRunner from '../query';
import LocationFinderQuery from '../locationFinderQuery';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocV1LocationFinder extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V1_LOCATION_FINDER)
        })
    }

    render() {
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Location Finder</h3>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V1_LOCATION_FINDER}?{'{'}query parameters{'}'}
                </code>
                <div className="description">
                    <p>
                        The Location Finder API enables you to search a knowledge base of over 30
                        million geographic locations. The parameters you use depend on the geographic
                        information you are seeking to obtain:
                    </p>
                    <ol className="indented-list">
                        <li>
                            <b>Forward Geocoding</b>: If you know the administrative district, such as
                            city/state/country, you can use the <code>query</code> parameter to obtain
                            its geographical data, including its coordinates.
                        </li>
                        <li>
                            <b>Reverse Lookup (Reverse Geocoding)</b>: If you have the latitude and
                            longitude of a location, you can use one of the following query parameters
                            (but not more than one at a time) to retrieve its geographical data:
                        </li>
                    </ol>
                    <table className="table-wrapper enrich">
                        <thead className="table-header enrich">
                            <tr>
                                <th>Parameter</th>
                                <th>Search region shape</th>
                                <th>Example value</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            <tr>
                                <td><code>bbox</code></td>
                                <td>Rectangle</td>
                                <td><code>-122.5,37.7,-122.3,37.9</code></td>
                                <td>Limits lookup to a box</td>
                            </tr>
                            <tr>
                                <td><code>centroid</code></td>
                                <td>Point</td>
                                <td><code>37.7749,-122.4194</code></td>
                                <td>Center of circular region</td>
                            </tr>
                            <tr>
                                <td><code>geometry</code></td>
                                <td>Custom shape</td>
                                <td><code>POLYGON((...))<br />POINT(...)</code></td>
                                <td>Custom spatial shape</td>
                            </tr>
                        </tbody>
                    </table>
                    <p>
                        Each request must include either:
                    </p>
                    <ul className="indented-list">
                        <li>
                            a) the <code>query</code> query parameter for forward geocoding (optionally specifying a <code>geometry</code>), or
                        </li>
                        <li>
                            b) a reverse lookup parameter, such as <code>bbox</code> or <code>centroid</code>
                        </li>
                    </ul>
                    <br/>
                    <p>
                        Refer to the <b>Examples</b> section for examples of using various query parameters to affect the results.
                    </p>
                </div>

                <h4 className="heading-2" id="lfv1_request">Request</h4>
                <div className="description enrich">
                    Obtain locations from the Location Finder API by constructing a URL with the specified parameters.
                </div>

                <h4 className="heading-3" id="lfv1_request_params">Query Parameters</h4>

                <div>
                    <p>
                        The following rules apply when sending requests to the LocationFinder API:
                    </p>
                    <ul className="indented-list">
                        <li>
                            You must specify at least one of the following parameters: <code>query</code>, <code>bbox</code>, or <code>centroid</code>. These parameters are mutually exclusive and cannot be used together. 
                        </li>
                        <li>
                            A radius (i.e., the rad parameter) is required when using <code>centroid</code>.
                        </li>
                        <li>
                            When defining a geometrical search area with <code>geometry</code>, you must specify a location (e.g., city/state) using the <code>query</code> query parameter.
                        </li>
                    </ul>
                </div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Required/Optional</th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">query</td>
                        <td className="meaning">
                            A location string to search for. Include administrative areas in order from smallest to largest, separated by commas.<br/><br/>

                            <b>Example</b>: <code>Cambridge, MA, USA</code>
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required<br/>
                            <br/>
                            Parameters <i>query</i>, <i>bbox</i>, and <i>centroid</i> are mutually exclusive.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">bbox</td>
                        <td className="meaning">
                            Limits the reverse lookup to a specific geographic region that is a rectangular area defined by two corners. Provide a comma-separated coordinate list (minlon,minlat,maxlon,maxlat) representing the reverse lookup region.<br/><br/>

                            <b>Example</b>: <code>-122.5,37.7,-122.3,37.9</code>
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required<br/>
                            <br/>
                            Parameters <i>query</i>, <i>bbox</i>, and <i>centroid</i> are mutually exclusive.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">centroid</td>
                        <td className="meaning">
                            Used together with a radius specified by the <code>rad</code> parameter to define a circular search area for reverse lookup. Provide comma-separated coordinates (lat,lon) representing the centroid of the reverse lookup region, and specify its extent using the <code>rad</code> parameter.<br/><br/>

                            <b>Example</b>: <code>37.7749,-122.4194</code>
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required.<br/>
                            <br/>
                            Parameters <i>query</i>, <i>bbox</i>, and <i>centroid</i> are mutually exclusive.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">rad</td>
                        <td className="meaning">
                            Applies only when used in conjunction with <code>centroid</code>. Specify the radius, in kilometers, of the circular lookup region around the centroid when the centroid parameter is specified.<br/><br/>

                            <b>Example</b>: <code>5</code> for 5 kilometers
                        </td>
                        <td className="type">Float</td>
                        <td className="constraints">Required when querying using the <i>centroid</i> parameter.</td>
                    </tr>
                    <tr>
                        <td className="attribute">outputVersion</td>
                        <td className="meaning">
                            Optionally specify the output version format. The accepted values are <code>2.2.0</code> and <code>4.0.0</code>. Note that <code>2.2.0</code> is for backward compatibility. This parameter is optional.<br/><br/>

                            <b>Default</b>: <code>4.0.0</code>
                        </td>
                        <td className="type">
                            String
                        </td>
                        <td className="constraints">
                            Optional
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">geometry</td>
                        <td className="meaning">
                            This allows for more complex and precise spatial shapes (e.g., polygons, lines, and multipoints). It must be a geometry string in <b>WKT (Well-Known Text)</b> format (see {miscUtils.createExternalLink('http://docs.opengeospatial.org/is/12-063r5/12-063r5.html')}).<br/><br/>

                            <b>Example</b>: <code>POLYGON((30 10, 40 40, 20 40, 10 20, 30 10))</code>
                        </td>
                        <td className="type">String<br/>
                            <br/>
                            Possible values constrained by ("WKT")<br/>                        
                        </td>
                        <td className="constraints">Optional</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2" id="lfv1_response">Response</h4>
                <div className="description enrich">
                    Upon successfully sending a request, the Location Finder API returns one or more locations based on your search criteria.
                </div>

                <h4 className="heading-3" id="lfv1_response_object">Response object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">locations</td>
                        <td className="meaning">List of location objects corresponding to the tagged geographic word or phrase 
                            from the input query.
                        </td>
                        <td className="type">See <a href="#lfv1_location_object">location object</a> description</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">enclosingViewBounds</td>
                        <td className="meaning">Object containing the minimum and maximum latitude and longitude of a 
                            bounding box that covers all the returned locations.
                        </td>
                        <td className="type">See <a href="#lfv1_bb_object">boundingBox object</a> description</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="lfv1_location_object">Location object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">id</td>
                        <td className="meaning">The knowledgebase globally unique identifier.</td>
                        <td className="type">Integer</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">type</td>
                        <td className="meaning">The type of entity</td>
                        <td className="type">String</td>
                        <td className="notes"><i>GEO</i> for location.</td>
                    </tr>
                    <tr>
                        <td className="attribute">label</td>
                        <td className="meaning">A finch canonical name for the entity.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">latitude</td>
                        <td className="meaning">The latitude of the geographic location.</td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">longitude</td>
                        <td className="meaning">The longitude of the geographic location.</td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">boundingBox</td>
                        <td className="meaning">The geographic location's bounding box.</td>
                        <td className="type">See <a href="#lfv1_bb_object">boundingBox object</a> description</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">population</td>
                        <td className="meaning">The population of the geographic location.</td>
                        <td className="type">Integer</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">dsgFeatureClass</td>
                        <td className="meaning">The DSG feature class of the geographic location. Specified as a single capitalized letter.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_classes">DSG feature classes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">dsgFeatureClassLabel</td>
                        <td className="meaning">The DSG feature class label. Omit if not available.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>  
                    <tr>
                        <td className="attribute">dsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>    
                    <tr>
                        <td className="attribute">dsgFeatureCodeLabel</td>
                        <td className="meaning">The DSG feature code display name.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr> 
                    <tr>
                        <td className="attribute">dsgFeatureCodeDescription</td>
                        <td className="meaning">The DSG feature code description.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryLabel</td>
                        <td className="meaning">The Finch canonical name for the country.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryFips</td>
                        <td className="meaning">The two-character FIPS country code.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/fips_codes">FIPS country codes</Link>.</td>
                    </tr>     
                    <tr>
                        <td className="attribute">countryGenc2</td>
                        <td className="meaning">GENC country digraph for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country digraphs</Link>.</td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryGenc3</td>
                        <td className="meaning">GENC country trigraph for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country trigraphs</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">countryGencCode</td>
                        <td className="meaning">GENC country numeric code for the location.</td>
                        <td className="type">Integer</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country numeric codes</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">countryDsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's country. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">admin1Label</td>
                        <td className="meaning">The Finch canonical name for the country's first administration level.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1Fips</td>
                        <td className="meaning">The four-character FIPS region code.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/fips_codes">FIPS region codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1Genc</td>
                        <td className="meaning">GENC province code for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC province codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1DsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's first administration level. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin2Label</td>
                        <td className="meaning">The Finch canonical name for the country's secondary administration level</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">admin2DsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's second administration level. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>

                    <tr>
                        <td className="attribute">boundingBoxDerivation</td>
                        <td className="meaning">Whether an enclosing geometry (polygon) is available.</td>
                        <td className="type">String<br/>
                            <br/>
                            Possible values ("Estimate", "Geometry")<br/>
                            <br/>
                            Estimate: When no enclosing geometry (polygon) available<br/>
                            <br/>
                            Geometry: When enclosing geometry (polygon) is available
                        </td>
                        <td className="notes"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="lfv1_bb_object">BoundingBox object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">minLatitude</td>
                        <td className="meaning">The geographic location's minimum latitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">maxLatitude</td>
                        <td className="meaning">The geographic location's maximum latitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">minLongitude</td>
                        <td className="meaning">The geographic location's minimum longitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">maxLongitude</td>
                        <td className="meaning">The geographic location's maximum longitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    </tbody>
                </table>

                <div>
                    <h3 id="#examples" className="heading-3">Examples</h3>

                     <h4 className="subheader2">Forward Geocoding by Name</h4>
                    <p>
                        The following requests uses the <code>query</code> query parameter to search for a place by its name when you want to get that place's details:
                    </p>

                    <div className="example-content host">
                        <pre>
                        GET {urlHandler.API_ENDPOINT_V1_LOCATION_FINDER}
                        <br />
                        Accept: application/json
                        <br />
                        Authorization: Bearer [YOUR-API-KEY]
                        <br />
                        <br />
                        </pre>
                        <pre>https://text-api.finchcomputing.com/api/v1/location-finder?query=London,UK</pre>
                    </div>
                    <HttpRunner
                        query="location-finder"
                        stated="query=London,UK"
                        response={
                        !this.state.dynamicSample
                            ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_LOCATION_FINDER_QUERY)
                            : null
                        }
                    />

                    <h4 className="subheader2">Forward Geocoding by Name and Shape</h4>

                    <p>
                        The following request uses the <code>query</code> and <code>geometry</code> query parameters to search using a location name and geometric shape.
                    </p>


                    <div className="example-content host">
                        <pre>
                        GET {urlHandler.API_ENDPOINT_V1_LOCATION_FINDER}
                        <br />
                        Accept: application/json
                        <br />
                        Authorization: Bearer [YOUR-API-KEY]
                        <br />
                        <br />
                        </pre>
                        <pre>https://text-api.finchcomputing.com/api/v1/location-finder?query=London,UK&geometry=POLYGON((30 10, 40 40, 20 40, 10 20, 30 10))</pre>
                    </div>

                    <br/>
                    <p>
                        <em>Use cases:</em>
                    </p>

                    <ul className="indented-list">
                        <li>
                            Disambiguating place names
                        </li>
                        <li>
                            Filtering results to a known region
                        </li>
                        <li>
                            Validating a place name belongs to a geometry
                        </li>
                        <li>
                            Combining administrative and custom boundaries
                        </li>
                        <li>
                            Spatial search constraints in user interfaces
                        </li>
                    </ul>


                    <HttpRunner
                        query="location-finder"
                        stated="query=London,UK"
                        response={
                        !this.state.dynamicSample
                            ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_LOCATION_FINDER_GEOMETRY)
                            : null
                        }
                    />


                    <h4 className="subheader2">Reverse Lookup by Bounding Box</h4>

                    <p>
                        The following request uses the <code>bbox</code> query parameter to search for locations within a rectangular area:
                    </p>

                    <div className="example-content host">
                        <pre>
                        GET {urlHandler.API_ENDPOINT_V1_LOCATION_FINDER}
                        <br />
                        Accept: application/json
                        <br />
                        Authorization: Bearer [YOUR-API-KEY]
                        <br />
                        <br />
                        </pre>
                        <pre>https://text-api.finchcomputing.com/api/v1/location-finder?bbox=-122.5,37.7,-122.3,37.9</pre>
                    </div>

                    <br/>
                    
                    <p>
                        The query string means:
                    </p>

                    <ul className="indented-list">
                        <li>
                            <b>minlon</b> = <code>-122.5</code> → western edge
                        </li>
                        <li>
                            <b>minlat</b> = <code>37.7</code> → southern edge
                        </li>
                        <li>
                            <b>maxlon</b> = <code>-122.3</code> → eastern edge
                        </li>
                        <li>
                            <b>maxlat</b> = <code>37.9</code> → northern edge
                        </li>
                    </ul>

                    <br/>
                    <p>
                        Hence, it defines a rectangle that spans:
                    </p>

                    <ul className="indented-list">
                        <li>
                            From longitude: <code>-122.5</code> to <code>-122.3</code>
                        </li>
                        <li>
                            And latitude: <code>37.7</code> to <code>37.9</code>
                        </li>
                    </ul>


                    <HttpRunner
                        query="location-finder"
                        stated="query=London,UK"
                        response={
                        !this.state.dynamicSample
                            ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_LOCATION_FINDER_BBOX)
                            : null
                        }
                    />



                    <h4 className="subheader2">Reverse Lookup by Radius</h4>

                    <p>
                        The following request uses the <code>centroid</code> and <code>rad</code> (i.e., "radius") query parameters to search a circular area by latitude/longitude.
                    </p>

                    <div className="example-content host">
                        <pre>
                        GET {urlHandler.API_ENDPOINT_V1_LOCATION_FINDER}
                        <br />
                        Accept: application/json
                        <br />
                        Authorization: Bearer [YOUR-API-KEY]
                        <br />
                        <br />
                        </pre>
                        <pre>https://text-api.finchcomputing.com/api/v1/location-finder?centroid=37.7749,-122.4194&rad=5</pre>
                    </div>

                    <br/>

                    <p>
                        The query string means:
                    </p>

                    <ul className="indented-list">
                        <li>
                            <code>centroid=37.7749,-122.4194</code> 
                        </li>
                        <p>
                            The <b>center point</b> of the search area.  In this case, it’s a point in <b>San Francisco, CA</b> (37.7749°N, 122.4194°W).
                        </p>
                        <li>
                            <code>rad=5</code>
                        </li>
                        <p>
                            The <b>radius in kilometers</b> from that centroid point defines a circular search area.
                        </p>
                    </ul>

                    <HttpRunner
                        query="location-finder"
                        stated="query=London,UK"
                        response={
                        !this.state.dynamicSample
                            ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_LOCATION_FINDER_RADIUS)
                            : null
                        }
                    />


                </div>
            </div>
        );
    }
}

export default DocV1LocationFinder;